/**
 * Distance helpers — convert raw meters into the unit the user prefers.
 *
 * The preference comes from the user-settings dialog (persisted to localStorage
 * by `UserSettingsDialog.js` as `userSettings.distance_unit`). If nothing's set
 * we default to miles to match the dialog's own default.
 *
 * Why localStorage and not a context: this is read in deeply nested render
 * paths (every road-trip block, every globe label) where a context would
 * cause unnecessary re-renders, and the value rarely changes.
 */

const KM_PER_MILE = 1.609344;

export const getDistanceUnit = () => {
  try {
    const cached = localStorage.getItem("userSettings");
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed?.distance_unit === "kilometers") return "kilometers";
    }
  } catch {
    /* corrupted JSON — fall through to default */
  }
  return "miles";
};

/**
 * Format raw meters as a string in the user's preferred unit.
 *   formatDistance(680000) -> "423 mi"  (default miles)
 *   formatDistance(680000) -> "680 km"  (when user set kilometers)
 *   formatDistance(0)      -> null
 */
export const formatDistance = (meters, options = {}) => {
  if (typeof meters !== "number" || !isFinite(meters) || meters <= 0) return null;
  const unit = options.unit || getDistanceUnit();
  if (unit === "kilometers") {
    const km = meters / 1000;
    // Below 10 km show one decimal so "1.7 km" doesn't read as "2 km" near
    // a city tour. Above 10 km integer is fine.
    return km < 10 ? `${km.toFixed(1)} km` : `${Math.round(km)} km`;
  }
  const mi = meters / 1000 / KM_PER_MILE;
  return mi < 10 ? `${mi.toFixed(1)} mi` : `${Math.round(mi)} mi`;
};
